import { ReactNode, Suspense, useCallback } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Result } from '@opengov/components-result';
import { Box } from '@mui/material';
import { DEFAULT_GENERIC_ERROR_SUBTITLE } from '../../utils/errorMessages';

interface QueryBoundaryProps {
  readonly children: ReactNode;
  readonly loadingFallback: ReactNode;
  readonly errorMessage: string;
  /**
   * Optional friendly subtitle override. If omitted, a generic
   * "Please try refreshing / contact your administrator" message is shown.
   * The underlying technical error is appended at the end of the subtitle so
   * support engineers can still capture it from a screenshot or log.
   */
  readonly errorSubtitle?: string;
}

function ErrorFallback({
  resetErrorBoundary,
  errorMessage,
  errorSubtitle,
}: FallbackProps & {
  readonly errorMessage: string;
  readonly errorSubtitle?: string;
}) {
  const subTitle = errorSubtitle ?? DEFAULT_GENERIC_ERROR_SUBTITLE;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Result
        title={errorMessage}
        subTitle={subTitle}
        size="large"
        status="error"
        actionButtons={[{ label: 'Try Again', onClick: resetErrorBoundary }]}
      />
    </Box>
  );
}

/**
 * Combined Suspense + ErrorBoundary wrapper for components that use
 * suspense-enabled query hooks (e.g. useSuspenseQuery).
 *
 * Renders `loadingFallback` while suspended, and a CDS Result error
 * state if a child throws.
 */
export function QueryBoundary({
  children,
  loadingFallback,
  errorMessage,
  errorSubtitle,
}: QueryBoundaryProps) {
  const renderFallback = useCallback(
    (props: FallbackProps) => (
      <ErrorFallback {...props} errorMessage={errorMessage} errorSubtitle={errorSubtitle} />
    ),
    [errorMessage, errorSubtitle]
  );

  return (
    <ErrorBoundary fallbackRender={renderFallback}>
      <Suspense fallback={loadingFallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}
